import React, { useEffect, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { track } from '@betterment/js-runtime';
import { Box, Button, Card, H2, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { vstack } from '@betterment/samba-styled-system/patterns';
import { SambaAutoLink } from '@betterment/shared-components';

import { ActiveRewardsDocument, ActiveRewardsQuery } from './graphql/query.activeRewards.generated';
import MessageGuidanceBox from './MessageGuidanceBox';
import RewardDetailsDialog from './RewardDetailsDialog';

import { trackClick } from '../../features/track';
import { MessageCtaLink, MessageCtaModal, MessageStyle } from '../../graphql/schema';
import MessageBox from '../../Shared/components-v2/message-box/message-box';
import { urlBuilder } from '../../Shared/urlBuilder';

type ActiveReward = ActiveRewardsQuery['activeRewards'][number];

const TRACKING_MODULE = 'SummaryRewardsSection';

const RewardHeader: React.FC<Partial<ActiveReward & { rewardName: string }>> = ({
  rewardName,
  periodEndDescription,
  rewardValueDescription,
  rewardContextDescription,
  isCompleted,
}) => {
  useEffect(() => {
    if (rewardName) {
      track('ElementViewed', {
        name: rewardName,
        module: TRACKING_MODULE,
      });
    }
  }, [rewardName]);

  return (
    <ListRow css={{ width: '100%', p: 'space.0', px: 'space.0', py: 'space.200' }}>
      <ListRow.LeadingContent>
        <Box css={{ display: 'flex' }}>
          <Text as="p" color="text" variant="text100">
            {rewardName}
          </Text>
        </Box>

        {periodEndDescription && (
          <Text as="p" color="text.weak" variant="text50">
            {periodEndDescription}
          </Text>
        )}
      </ListRow.LeadingContent>

      {rewardValueDescription && (
        <ListRow.TrailingContent>
          <Text color="text" variant="text100">
            {rewardValueDescription}

            {rewardContextDescription && (
              <Text as="span" css={{ display: 'block' }} variant="text50" color="text.weak">
                {rewardContextDescription}
              </Text>
            )}
          </Text>
        </ListRow.TrailingContent>
      )}

      <ListRow.TrailingAsset css={{ textDecoration: 'underline' }}>
        <Icon color="icon" name="CaretRight" size="small" />
      </ListRow.TrailingAsset>
    </ListRow>
  );
};

const RewardsCard: React.FC = () => {
  const { data } = useGraphQLQuery(ActiveRewardsDocument, { throwOnError: true });
  const [modalId, setModalId] = useState('');

  if (!data || data.activeRewards.length === 0) return null;
  const { activeRewards } = data;

  return (
    <Box>
      <Card>
        <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
          <H2 variant="title200">Rewards</H2>
          <SambaAutoLink to={urlBuilder.appEarnedRewards.absolute()}>See all</SambaAutoLink>
        </Card.Header>
        {activeRewards.map((activeReward, index) => {
          const {
            isCompleted,
            name: rewardName,
            periodEndDescription,
            rewardContextDescription,
            rewardValueDescription,
            tiers,
            message,
          } = activeReward;

          const isLastItem = activeRewards.length - 1 === index;

          return (
            <Box key={activeReward.id}>
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
              <Card.Content
                className={vstack({
                  gap: 'space.100',
                  alignItems: 'stretch',
                  px: 'space.300',
                  paddingBottom: isLastItem ? 'space.300' : '',
                })}
              >
                <Button
                  onClick={() => {
                    trackClick({ name: rewardName, module: TRACKING_MODULE });
                    setModalId(activeReward.id);
                  }}
                  variant="ghostPrimary"
                  css={{ px: 'space.0', _hover: { background: 'background.transparent' } }}
                >
                  <RewardHeader
                    isCompleted={isCompleted}
                    rewardName={rewardName}
                    periodEndDescription={periodEndDescription}
                    rewardContextDescription={rewardContextDescription}
                    rewardValueDescription={rewardValueDescription}
                    tiers={tiers}
                  />
                </Button>

                {message && MessageStyle.Advice === message.style && (
                  <MessageGuidanceBox
                    cta={message.cta as MessageCtaLink | MessageCtaModal}
                    title={message.title}
                    body={message.body}
                  />
                )}

                {message && MessageStyle.Success === message.style && (
                  <MessageBox messages={[{ ...message, isDismissible: true }]} location="" />
                )}
              </Card.Content>
              <RewardDetailsDialog
                activeReward={activeReward}
                openModal={modalId === activeReward.id}
                onOpenChange={() => setModalId('')}
              />
            </Box>
          );
        })}
      </Card>
    </Box>
  );
};

export default RewardsCard;
